// Session-storage keys for the OAuth `state` (XSRF token) and PKCE
// `code_verifier`. sessionStorage survives the Cognito redirect but is scoped
// to the tab, so a parallel login in another tab cannot consume this one's
// verifier. See
// https://developers.google.com/identity/openid-connect/openid-connect#createxsrftoken
const SSO_STATE_KEY = "alaffia.sso.state";
const SSO_CODE_VERIFIER_KEY = "alaffia.sso.codeVerifier";
const SSO_PROVIDER_CONFIG_KEY = "alaffia.sso.providerConfig";

interface SsoProviderConfig {
  domain: string;
  clientId: string;
}

const base64UrlEncode = (bytes: Uint8Array): string => {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const randomBase64Url = (byteLength: number): string => {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
};

const sha256Base64Url = async (input: string): Promise<string> => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(input),
  );
  return base64UrlEncode(new Uint8Array(digest));
};

interface SsoAuthorizationRequest {
  state: string;
  codeChallenge: string;
}

/**
 * Generate a fresh `state` and PKCE `code_verifier`, persist both — plus the
 * Cognito provider config the SSO flow will need at the callback — in
 * sessionStorage, and return the values needed for the authorize URL. Callers
 * must send `state` and the S256 `codeChallenge` on the authorize request;
 * `consumeSsoAuthorizationRequest` returns the verifier for token exchange.
 */
export const createSsoAuthorizationRequest = async (
  providerConfig: SsoProviderConfig,
): Promise<SsoAuthorizationRequest> => {
  const state = randomBase64Url(32);
  const codeVerifier = randomBase64Url(64);
  const codeChallenge = await sha256Base64Url(codeVerifier);
  sessionStorage.setItem(SSO_STATE_KEY, state);
  sessionStorage.setItem(SSO_CODE_VERIFIER_KEY, codeVerifier);
  sessionStorage.setItem(
    SSO_PROVIDER_CONFIG_KEY,
    JSON.stringify(providerConfig),
  );
  return { state, codeChallenge };
};

interface ConsumedSsoAuthorizationRequest {
  state: string | null;
  codeVerifier: string | null;
  providerConfig: SsoProviderConfig | null;
}

/**
 * Return the persisted `state`/`codeVerifier`/`providerConfig` from
 * `createSsoAuthorizationRequest`, then clear them. All values are single-use:
 * consuming twice returns nulls the second time.
 */
export const consumeSsoAuthorizationRequest =
  (): ConsumedSsoAuthorizationRequest => {
    const state = sessionStorage.getItem(SSO_STATE_KEY);
    const codeVerifier = sessionStorage.getItem(SSO_CODE_VERIFIER_KEY);
    const rawProviderConfig = sessionStorage.getItem(SSO_PROVIDER_CONFIG_KEY);
    sessionStorage.removeItem(SSO_STATE_KEY);
    sessionStorage.removeItem(SSO_CODE_VERIFIER_KEY);
    sessionStorage.removeItem(SSO_PROVIDER_CONFIG_KEY);

    let providerConfig: SsoProviderConfig | null = null;
    if (rawProviderConfig) {
      try {
        providerConfig = JSON.parse(rawProviderConfig) as SsoProviderConfig;
      } catch {
        providerConfig = null;
      }
    }

    return { state, codeVerifier, providerConfig };
  };
